// LiveMonitorList.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box, Card, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, CircularProgress, Button, Divider } from "@mui/material";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import moment from "moment";
import Swal from "sweetalert2";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// API Services
import { getLiveSnapshot, postArrived, postAnomaly, postGhostBus } from "modules/stopmonitor/api";

// Components and Utilities
import DepartureRow from "./DepartureRow";
import { feedConfig, createPopupHtml } from "./LiveMonitorUtils";

export default function LiveMonitorList({ stopId = "2000167", onSelectTrip, selectedMatchKey }) {
    const activeStopId = String(stopId);

    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(Date.now());
    const [secondsSinceFetch, setSecondsSinceFetch] = useState(0);
    const [stopInfo, setStopInfo] = useState(null);
    const [reportedTrips, setReportedTrips] = useState({});

    // NEW: Replaced timeMode with listFilter state
    const [listFilter, setListFilter] = useState("upcoming"); // 'upcoming', 'past', or 'saved'

    const masterTripsRef = useRef(new Map());
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const layersAddedRef = useRef(false);

    const activePopupRef = useRef(null);
    const activeMatchKeyRef = useRef(null);

    useEffect(() => {
        const timer = setInterval(() => {
            setSecondsSinceFetch(Math.floor((Date.now() - lastUpdated) / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, [lastUpdated]);

    // ARRIVAL HANDLER
    const handleTriggerArrival = async (departureItem) => {
        const currentEpoch = moment().unix();
        const formattedTime = moment.unix(currentEpoch).format("h:mm:ss a");

        const modalDescription = `
          <div style="text-align: left; font-family: sans-serif; font-size: 14px; color: #475569; line-height: 1.6;">
            <p style="margin-bottom: 8px;"><strong>Route Name:</strong> Route ${departureItem.routeShortName}</p>
            <p style="margin-bottom: 8px;"><strong>Destination:</strong> ${departureItem.tripHeadsign}</p>
            <p style="margin-bottom: 8px;"><strong>Arrival Metric:</strong> ${formattedTime}</p>
          </div>
        `;

        const result = await Swal.fire({
            title: "Confirm Vehicle Arrival?",
            html: modalDescription,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Confirm",
        });

        if (result.isConfirmed) {
            try {
                await postArrived(departureItem.matchKey, activeStopId);
                setReportedTrips(prev => ({ ...prev, [departureItem.matchKey]: 'arrived' }));
                Swal.fire({ title: "Logged!", text: "Arrival flagged successfully.", icon: "success", timer: 1500, showConfirmButton: false });
            } catch (e) {
                Swal.fire("Error", "Could not log arrival.", "error");
            }
        }
    };

    // ANOMALY HANDLER
    const handleTriggerAnomaly = async (departureItem) => {
        const modalDescription = `
          <div style="text-align: left; font-family: sans-serif; font-size: 14px; color: #475569;">
            <p><strong>Route Name:</strong> Route ${departureItem.routeShortName}</p>
            <p>Flag this feed data as erratic or severely decoupled from reality?</p>
          </div>
        `;

        const result = await Swal.fire({
            title: "Report Stream Anomaly?",
            html: modalDescription,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#b91c1c",
            confirmButtonText: "Report",
        });

        if (result.isConfirmed) {
            try {
                await postAnomaly(departureItem.matchKey, activeStopId);
                setReportedTrips(prev => ({ ...prev, [departureItem.matchKey]: 'anomaly' }));
                Swal.fire({ title: "Reported!", text: "Anomaly registered.", icon: "success", timer: 1500, showConfirmButton: false });
            } catch (e) {
                Swal.fire("Error", "Could not report anomaly.", "error");
            }
        }
    };

    // GHOST BUS HANDLER
    const handleTriggerGhostBus = async () => {
        const { value: formValues, isConfirmed } = await Swal.fire({
            title: 'Report Ghost Bus',
            html: `
            <input id="swal-route" class="swal2-input" placeholder="Route Short Name">
            <input id="swal-headsign" class="swal2-input" placeholder="Destination / Headsign">
          `,
            focusConfirm: false,
            showCancelButton: true,
            preConfirm: () => {
                return {
                    route: document.getElementById('swal-route').value,
                    headsign: document.getElementById('swal-headsign').value
                };
            }
        });

        if (isConfirmed && formValues) {
            try {
                await postGhostBus({
                    stopId: activeStopId,
                    routeShortName: formValues.route,
                    tripHeadsign: formValues.headsign
                });
                Swal.fire({ title: 'Logged!', text: 'Ghost bus submitted.', icon: 'success', timer: 1500, showConfirmButton: false });
            } catch (e) {
                Swal.fire("Error", "Could not log ghost bus.", "error");
            }
        }
    };

    // ROBUST NETWORK POLLER
    useEffect(() => {
        if (!activeStopId) return;

        const fetchTransitData = async () => {
            try {
                const snapshot = await getLiveSnapshot(activeStopId);

                const departuresList =
                    snapshot?.departures ||
                    snapshot?.data?.departures ||
                    (Array.isArray(snapshot?.data) ? snapshot.data : []);

                if (Array.isArray(departuresList)) {
                    masterTripsRef.current.clear();
                    const syncedReportedTrips = {};

                    departuresList.forEach((dep) => {
                        if (dep.arrivalEpoch) {
                            syncedReportedTrips[dep.matchKey] = 'arrived';
                        } else if (dep.isAnomaly) {
                            syncedReportedTrips[dep.matchKey] = 'anomaly';
                        }

                        masterTripsRef.current.set(dep.matchKey, {
                            ...dep,
                            feed1Active: !dep.isClosed && dep.feed1Epoch !== null,
                            feed2Active: !dep.isClosed && dep.feed2Epoch !== null,
                            feed3Active: false,
                            feed1Realtime: true,
                            feed2Realtime: dep.isRealtime,
                            position: dep.position || null
                        });
                    });

                    setReportedTrips(prev => ({ ...prev, ...syncedReportedTrips }));
                    setStopInfo(prev => prev || { lat: -33.883665, lon: 151.203342, name: "Live Stop" });
                    setLastUpdated(Date.now());
                }
            } catch (error) {
                console.error("Scraper API ingest breakdown:", error);
            } finally {
                setLoading(false);
            }
        };
         

        fetchTransitData(); 
        const networkInterval = setInterval(fetchTransitData, 10 * 1000);
        return () => clearInterval(networkInterval);
    }, [activeStopId]);

    // UPDATED: LIST HYDRATION WITH STRICT STATE FILTERS & SORTING
    const normalizedDepartures = useMemo(() => {
        const processedList = [];
        const currentUnix = moment().unix();

        masterTripsRef.current.forEach((trip) => {
            const scheduledEpoch = Number(trip.matchKey.split("_")[1]);

            const f1Secs = trip.feed1Epoch ? trip.feed1Epoch - currentUnix : null;
            const f2Secs = trip.feed2Epoch ? trip.feed2Epoch - currentUnix : null;

            // Fallback to static scheduled time to prevent sorting breaks
            const sortTime = f2Secs ?? f1Secs ?? (scheduledEpoch - currentUnix);

            const isArrived = !!trip.arrivalEpoch;
            const isClosed = !!trip.isClosed;

            

            const dynamicEntry = { ...trip, sortTime };

            if (listFilter === "upcoming") {
                // CURRENT: Actively in the feed (not closed) and not manually marked arrived
                if (!isClosed && !isArrived) {
                    processedList.push(dynamicEntry);
                }
            } else if (listFilter === "past") {
                // PAST: Dropped out of the feed naturally (closed) but was NEVER marked as arrived
                if (isClosed && !isArrived) {
                    processedList.push(dynamicEntry);
                }
            } else if (listFilter === "saved") {
                // SAVED: Exclusively trips that a user actively marked as arrived
                if (isArrived) {
                    processedList.push(dynamicEntry);
                }
            }
        });

        return processedList.sort((a, b) => {
            if (listFilter === "upcoming") return a.sortTime - b.sortTime;
            return b.sortTime - a.sortTime;
        });
    }, [lastUpdated, listFilter]);

    const departuresRef = useRef();
    departuresRef.current = normalizedDepartures;

    const mapboxFeatures = useMemo(() => {
        return normalizedDepartures
            .filter((dep) => dep.position?.latitude && dep.position?.longitude)
            .filter((dep) => dep.feed1Active || dep.feed2Active || dep.feed3Active)
            .map((dep) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [dep.position.longitude, dep.position.latitude]
                },
                properties: {
                    matchKey: dep.matchKey,
                    bearing: Number(dep.position.bearing ?? 0),
                    routeShortName: dep.routeShortName
                }
            }));
    }, [normalizedDepartures]);

    // MAPBOX INITIALIZATION
    useEffect(() => {
        if (loading || !stopInfo) return;
        if (!mapContainerRef.current || mapRef.current) return;

        const { lon, lat } = stopInfo;

        const mapInstance = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: "mapbox://styles/mapbox/light-v11",
            center: [lon, lat],
            zoom: 14.5,
        });

        mapInstance.addControl(new mapboxgl.NavigationControl(), "top-right");

        mapInstance.on("load", () => {
            mapInstance.addSource("vehicles", {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] },
                cluster: true,
                clusterMaxZoom: 15,
                clusterRadius: 45
            });

            mapInstance.addLayer({
                id: "cluster-groups",
                type: "circle",
                source: "vehicles",
                filter: ["has", "point_count"],
                paint: {
                    "circle-color": "#344767",
                    "circle-radius": ["step", ["get", "point_count"], 18, 4, 22, 10, 26],
                    "circle-stroke-width": 3,
                    "circle-stroke-color": "#ffffff"
                }
            });

            mapInstance.addLayer({
                id: "cluster-group-labels",
                type: "symbol",
                source: "vehicles",
                filter: ["has", "point_count"],
                layout: {
                    "text-field": "{point_count}",
                    "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                    "text-size": 12
                },
                paint: { "text-color": "#ffffff" }
            });

            mapInstance.addLayer({
                id: "unclustered-vehicle-circles",
                type: "circle",
                source: "vehicles",
                filter: ["!", ["has", "point_count"]],
                paint: {
                    "circle-color": "#00b5ef",
                    "circle-radius": 16,
                    "circle-stroke-width": 2,
                    "circle-stroke-color": "#ffffff"
                }
            });

            mapInstance.addLayer({
                id: "unclustered-vehicle-labels",
                type: "symbol",
                source: "vehicles",
                filter: ["!", ["has", "point_count"]],
                layout: {
                    "text-field": ["get", "routeShortName"],
                    "text-size": 11,
                    "text-allow-overlap": true,
                    "text-ignore-placement": true
                },
                paint: { "text-color": "#ffffff" }
            });

            const arrowSvg = `<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
        <path d="M30 1 L41 20 L19 20 Z" fill="#00b5ef" stroke="#ffffff" stroke-width="2" stroke-linejoin="round" />
      </svg>`;
            const img = new Image(60, 60);
            img.onload = () => {
                if (!mapInstance || !mapInstance.getStyle()) return;
                if (!mapInstance.hasImage("direction-arrow")) {
                    mapInstance.addImage("direction-arrow", img);
                    mapInstance.addLayer({
                        id: "unclustered-vehicle-arrows",
                        type: "symbol",
                        source: "vehicles",
                        filter: ["!", ["has", "point_count"]],
                        layout: {
                            "icon-image": "direction-arrow",
                            "icon-rotate": ["get", "bearing"],
                            "icon-rotation-alignment": "map",
                            "icon-allow-overlap": true,
                            "icon-ignore-placement": true,
                            "icon-anchor": "center"
                        }
                    }, "unclustered-vehicle-circles");
                }
            };
            img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(arrowSvg);

            const stopEl = document.createElement("div");
            stopEl.style.cssText = "background-color: #344767; width: 24px; height: 24px; border-radius: 50%; border: 4px solid #FFFFFF; box-shadow: 0px 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; font-size: 11px; color: white; font-weight: bold;";
            stopEl.innerHTML = "S";
            new mapboxgl.Marker(stopEl).setLngLat([lon, lat]).addTo(mapInstance);

            const source = mapInstance.getSource("vehicles");
            if (source) {
                source.setData({ type: "FeatureCollection", features: mapboxFeatures });
            }

            mapInstance.on("click", "cluster-groups", (e) => {
                const features = mapInstance.queryRenderedFeatures(e.point, { layers: ["cluster-groups"] });
                const clusterId = features[0].properties.cluster_id;

                mapInstance.getSource("vehicles").getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err) return;
                    mapInstance.easeTo({ center: features[0].geometry.coordinates, zoom: zoom + 0.5 });
                });
            });

            mapInstance.on("click", "unclustered-vehicle-circles", (e) => {
                const feature = e.features[0];
                const coordinates = feature.geometry.coordinates.slice();
                const key = feature.properties.matchKey;

                const liveDep = departuresRef.current.find((d) => d.matchKey === key);
                if (!liveDep) return;

                if (activePopupRef.current) activePopupRef.current.remove();

                const html = createPopupHtml(liveDep, "absolute", "feed2", moment().unix());

                const popup = new mapboxgl.Popup({ offset: 12, closeButton: false })
                    .setLngLat(coordinates)
                    .setHTML(html)
                    .addTo(mapInstance);

                activePopupRef.current = popup;
                activeMatchKeyRef.current = key;

                popup.on("close", () => {
                    if (activeMatchKeyRef.current === key) {
                        activePopupRef.current = null;
                        activeMatchKeyRef.current = null;
                    }
                });
            });

            const togglePointer = (enter) => mapInstance.getCanvas().style.cursor = enter ? "pointer" : "";
            mapInstance.on("mouseenter", "cluster-groups", () => togglePointer(true));
            mapInstance.on("mouseleave", "cluster-groups", () => togglePointer(false));
            mapInstance.on("mouseenter", "unclustered-vehicle-circles", () => togglePointer(true));
            mapInstance.on("mouseleave", "unclustered-vehicle-circles", () => togglePointer(false));

            layersAddedRef.current = true;
            mapRef.current = mapInstance;
        });

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                layersAddedRef.current = false;
            }
        };
    }, [loading, stopInfo]);

    useEffect(() => {
        if (!mapRef.current || !layersAddedRef.current) return;

        const source = mapRef.current.getSource("vehicles");
        if (source) {
            source.setData({ type: "FeatureCollection", features: mapboxFeatures });
        }

        if (activeMatchKeyRef.current && activePopupRef.current) {
            const activeDep = departuresRef.current.find((d) => d.matchKey === activeMatchKeyRef.current);
            if (activeDep) {
                const html = createPopupHtml(activeDep, "absolute", "feed2", moment().unix());
                activePopupRef.current.setHTML(html);
            } else {
                activePopupRef.current.remove();
            }
        }
    }, [mapboxFeatures]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 4 }}>
                <CircularProgress size={36} sx={{ color: "#344767" }} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* TOP HALF: Mapbox Container */}
            <Box sx={{                 display: { xxs: "none", md: "block" }, height: "35vh", minHeight: "250px", position: "relative", flexShrink: 0 }}>
                <div ref={mapContainerRef} style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }} />
            </Box>

            <Divider />

            {/* BOTTOM HALF: Live Departures List */}
            <SoftBox p={{ xxs: 2, sm: 3 }} sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <SoftTypography variant="h6" fontWeight="bold" color="dark">
                            Live Departures
                        </SoftTypography>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleTriggerGhostBus}
                            sx={{
                                backgroundColor: "#64748b",
                                color: "#fff",
                                '&:hover': { backgroundColor: "#475569" },
                                textTransform: "none"
                            }}
                        >
                            Log Ghost Bus
                        </Button>
                    </Box>

                    <SoftTypography variant="caption" color="text" display="block">
                        Stop ID: <strong>{activeStopId}</strong> {stopInfo?.name && `- ${stopInfo.name}`} <br />
                        Last Fetched: {secondsSinceFetch} seconds ago
                    </SoftTypography>
                </Box>

                {/* UPDATED: Departures List Filter Control */}
                <Box sx={{ bgcolor: "#f8fafc", p: 1.5, borderRadius: "8px", mb: 2, display: "flex", flexDirection: "column", gap: 1, border: "1px solid #e2e8f0" }}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend" style={{ fontSize: "11px", fontWeight: "700", color: "#475569" }}>
                            FILTER DEPARTURES
                        </FormLabel>
                        <RadioGroup row value={listFilter} onChange={(e) => setListFilter(e.target.value)}>
                            <FormControlLabel value="upcoming" control={<Radio size="small" sx={{ color: "#344767" }} />} label={<SoftTypography variant="caption">Current (In Feed)</SoftTypography>} />
                            <FormControlLabel value="past" control={<Radio size="small" sx={{ color: "#344767" }} />} label={<SoftTypography variant="caption">Past (Closed)</SoftTypography>} />
                            <FormControlLabel value="saved" control={<Radio size="small" sx={{ color: "#344767" }} />} label={<SoftTypography variant="caption">Saved (Arrived)</SoftTypography>} />
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Divider />

                <SoftBox mt={1}>
                    {normalizedDepartures.length === 0 ? (
                        <SoftTypography variant="body2" color="text" sx={{ p: 2, textAlign: "center" }}>
                            No departures match the current filter.
                        </SoftTypography>
                    ) : (
                        normalizedDepartures.map((dep) => (
                            <Box
                                key={dep.matchKey}
                                onClick={() => onSelectTrip && onSelectTrip(dep.matchKey)}
                                sx={{
                                    backgroundColor: selectedMatchKey === dep.matchKey ? "#e2e8f0" : "transparent",
                                    borderRadius: 2,
                                    px: 1,
                                    cursor: "pointer",
                                    transition: "background-color 0.2s ease",
                                    '&:hover': {
                                        backgroundColor: selectedMatchKey === dep.matchKey ? "#e2e8f0" : "#f8fafc"
                                    }
                                }}
                            >
                                <DepartureRow
                                    dep={dep}
                                    timeMode="absolute"
                                    baseFeed="feed2"
                                    onArrival={handleTriggerArrival}
                                    onAnomaly={handleTriggerAnomaly}
                                    reportStatus={reportedTrips[dep.matchKey]}
                                />
                            </Box>
                        ))
                    )}
                </SoftBox>
            </SoftBox>
        </Box>
    );
}